import { useCallback, useEffect, useRef, useState } from "react";
import { registerCastScoreboardSender } from "src/state/modules/cast-scoreboard";

// Google Cast (Chromecast) support, Chrome/Edge only. The Cast receiver is a separate device
// that fetches the HLS URL itself, so the media URL must be publicly reachable and CORS-enabled
// (the backend serves `Access-Control-Allow-Origin: *` on the playlist/segment routes for this).
//
// LG webOS TVs do NOT have Chromecast built-in, so this path reaches the dedicated Chromecast
// dongle; the webOS TV is reached via AirPlay instead (see useAirPlay).

const CAST_SDK_SRC = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";

// A custom Cast receiver (registered in the Google Cast console, pointed at our hosted
// /cast-receiver.html) is what lets us draw the live scoreboard on the TV. When none is
// configured we use Google's default media receiver — casting still works, just no on-TV overlay.
const CUSTOM_RECEIVER_APP_ID = process.env.REACT_APP_CAST_RECEIVER_APP_ID || "";
// Custom message channel the receiver listens on for scoreboard updates.
const SCOREBOARD_NAMESPACE = "urn:x-cast:com.gcstats.scoreboard";

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyWindow = typeof window & {
  cast?: any;
  chrome?: any;
  __onGCastApiAvailable?: (isAvailable: boolean) => void;
};

// Load the Cast Sender framework exactly once per page. The script calls
// window.__onGCastApiAvailable(isAvailable) once it has decided whether Cast is usable.
let castLoadPromise: Promise<boolean> | null = null;
function loadCastSdk(): Promise<boolean> {
  if (castLoadPromise) return castLoadPromise;
  castLoadPromise = new Promise<boolean>((resolve) => {
    const w = window as AnyWindow;
    if (w.cast?.framework) {
      resolve(true);
      return;
    }
    w.__onGCastApiAvailable = (isAvailable: boolean) => resolve(isAvailable);
    const script = document.createElement("script");
    script.src = CAST_SDK_SRC;
    script.async = true;
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
  return castLoadPromise;
}

interface CastMedia {
  title?: string;
  poster?: string;
  isLive?: boolean;
}

export function useGoogleCast(src: string, media: CastMedia) {
  const [available, setAvailable] = useState(false);
  const [casting, setCasting] = useState(false);

  // The latest source/metadata, read at load time so a cast started after the source changed
  // (e.g. a different clip) sends the current media rather than a stale closure.
  const mediaRef = useRef({ src, ...media });
  mediaRef.current = { src, ...media };

  useEffect(() => {
    let cancelled = false;
    let context: any;
    let onCastState: (() => void) | undefined;

    loadCastSdk().then((ok) => {
      if (!ok || cancelled) return;
      const w = window as AnyWindow;
      const { cast, chrome } = w;
      if (!cast?.framework || !chrome?.cast) return;

      context = cast.framework.CastContext.getInstance();
      context.setOptions({
        receiverApplicationId: CUSTOM_RECEIVER_APP_ID || chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        // Only auto-rejoin sessions this site started, so we don't hijack an unrelated cast.
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });

      onCastState = () => {
        const state = context.getCastState();
        const connected = state === cast.framework.CastState.CONNECTED;
        setAvailable(state !== cast.framework.CastState.NO_DEVICES_AVAILABLE);
        setCasting(connected);

        // While a custom-receiver session is live, forward scoreboard updates to the TV. The
        // default receiver doesn't understand our namespace, so only wire this up for a custom one.
        if (CUSTOM_RECEIVER_APP_ID) {
          if (connected) {
            const session = context.getCurrentSession();
            registerCastScoreboardSender((sb) => {
              try {
                session?.sendMessage(SCOREBOARD_NAMESPACE, sb ?? { type: "clear" });
              } catch {
                /* receiver not listening yet, or session ending */
              }
            });
          } else {
            registerCastScoreboardSender(null);
          }
        }
      };
      context.addEventListener(cast.framework.CastContextEventType.CAST_STATE_CHANGED, onCastState);
      onCastState();
    });

    return () => {
      cancelled = true;
      if (CUSTOM_RECEIVER_APP_ID) registerCastScoreboardSender(null);
      if (context && onCastState) {
        const { cast } = window as AnyWindow;
        context.removeEventListener(cast.framework.CastContextEventType.CAST_STATE_CHANGED, onCastState);
      }
    };
  }, []);

  // Hand the current media to the active Cast session's receiver.
  const loadMedia = useCallback((startTime: number) => {
    const { cast, chrome } = window as AnyWindow;
    const session = cast?.framework.CastContext.getInstance().getCurrentSession();
    if (!session) return;

    const { src: url, title, poster, isLive } = mediaRef.current;
    const mediaInfo = new chrome.cast.media.MediaInfo(url, "application/x-mpegurl");
    mediaInfo.streamType = isLive ? chrome.cast.media.StreamType.LIVE : chrome.cast.media.StreamType.BUFFERED;

    const metadata = new chrome.cast.media.GenericMediaMetadata();
    metadata.title = title || "GameChanger";
    if (poster) metadata.images = [new chrome.cast.Image(poster)];
    mediaInfo.metadata = metadata;

    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    if (!isLive && startTime > 0) request.currentTime = startTime;
    session.loadMedia(request).catch(() => {
      /* receiver couldn't load the stream (CORS/format) — leave the local player as-is */
    });
  }, []);

  // Open the device chooser if needed, then push the current media to the receiver.
  const startCasting = useCallback(
    async (startTime = 0) => {
      const { cast } = window as AnyWindow;
      if (!cast?.framework) return;
      const context = cast.framework.CastContext.getInstance();
      try {
        if (context.getCastState() !== cast.framework.CastState.CONNECTED) {
          await context.requestSession();
        }
        loadMedia(startTime);
      } catch {
        /* user dismissed the device chooser */
      }
    },
    [loadMedia]
  );

  const stopCasting = useCallback(() => {
    const { cast } = window as AnyWindow;
    cast?.framework.CastContext.getInstance().endCurrentSession(true);
  }, []);

  return { available, casting, startCasting, stopCasting };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
